import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Mail, Droplet, ArrowLeft, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (e) {
    return "—";
  }
}

export default function AdminLeads() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    if (ready && user && user.role !== "admin") navigate("/dashboard");
  }, [ready, user, navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/leads").then(({ data }) => setLeads(data.leads || [])).catch(() => setLeads([]));
    }
  }, [user]);

  const exportCsv = () => {
    if (!leads?.length) return;
    const rows = [["Email", "Leak", "Captured"], ...leads.map((l) => [l.email, l.leak_name || "", l.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "oxytocin-leads.csv"; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  return (
    <div className="min-h-screen bg-wash">
      <SiteNav variant="dark" />
      <section className="relative velvet bg-purple-deep pt-[72px]">
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-3">Admin · Warm leads</p>
          <h1 className="font-heading text-white text-[clamp(1.8rem,4vw,2.8rem)] leading-tight">Leak diagnostic leads</h1>
          <p className="mt-3 text-white/75 text-[15px] max-w-2xl">
            Everyone who asked for their leak result by email. Follow up personally — they've already told you where their team leaks.
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between gap-4 mb-6">
          <p className="text-purple font-semibold text-[15px]" data-testid="leads-count">
            {leads === null ? "Loading…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`}
          </p>
          <button
            onClick={exportCsv}
            disabled={!leads?.length}
            className="inline-flex items-center gap-2 bg-purple-deep text-cream font-semibold rounded-full px-5 py-2.5 text-[14px] hover:bg-purple transition-colors disabled:opacity-50"
            data-testid="leads-export-btn"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {leads === null ? (
          <div className="rounded-2xl border border-line bg-white p-14 text-center" data-testid="leads-loading">
            <p className="text-lav font-heading italic text-xl animate-pulse">Loading leads…</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="rounded-2xl border border-line bg-white p-14 text-center" data-testid="leads-empty">
            <Inbox className="w-10 h-10 text-lav mx-auto mb-4" />
            <p className="font-heading text-purple text-xl">No leads yet</p>
            <p className="text-ink/60 text-[14px] mt-1">They'll appear here the moment someone asks for their result by email.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-line bg-white overflow-hidden" data-testid="leads-table">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-wash text-[11px] uppercase tracking-[0.14em] text-lav">
                  <th className="px-5 py-3.5 font-bold">Email</th>
                  <th className="px-5 py-3.5 font-bold">Leak</th>
                  <th className="px-5 py-3.5 font-bold">Captured</th>
                </tr>
              </thead>
              <tbody>
                {(leads || []).map((l, i) => (
                  <tr key={i} className="border-t border-line hover:bg-cream/40 transition-colors" data-testid={`lead-row-${i}`}>
                    <td className="px-5 py-3.5">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 text-purple-mid font-medium hover:text-purple">
                        <Mail className="w-3.5 h-3.5 text-gold-dark" /> {l.email}
                      </a>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-2 text-ink/80 text-[14px]">
                        <Droplet className="w-3.5 h-3.5 text-gold-dark" /> {l.leak_name || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-ink/60 text-[14px]">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Link to="/dashboard" className="mt-8 inline-flex items-center gap-2 text-purple-mid font-semibold hover:text-purple text-[14px]" data-testid="leads-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to my course
        </Link>
      </main>
    </div>
  );
}
